/**
 * Date and number formatting utilities.
 * Uses Intl API (no external library needed).
 * See: docs/I18N-GUIDE.md §5
 */

export function formatDate(date: string | Date, locale: string = 'en'): string {
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d)
}

export function formatDateTime(date: string | Date, locale: string = 'en'): string {
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

export function formatRelative(date: string | Date, locale: string = 'en'): string {
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return ''

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  const diff = d.getTime() - Date.now()
  const seconds = Math.round(diff / 1000)
  const minutes = Math.round(seconds / 60)
  const hours = Math.round(minutes / 60)
  const days = Math.round(hours / 24)
  const weeks = Math.round(days / 7)
  const months = Math.round(days / 30)

  if (Math.abs(seconds) < 60) return rtf.format(seconds, 'second')
  if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute')
  if (Math.abs(hours) < 24) return rtf.format(hours, 'hour')
  if (Math.abs(days) < 7) return rtf.format(days, 'day')
  if (Math.abs(weeks) < 4) return rtf.format(weeks, 'week')
  return rtf.format(months, 'month')
}

export function formatNumber(n: number, locale: string = 'en'): string {
  return new Intl.NumberFormat(locale).format(n)
}

/**
 * Offline letter-avatar for a domain (data-URI SVG, deterministic color).
 * Deliberately NOT Google's s2 favicon service: every card render would leak
 * the viewed domains to a third party, and it breaks offline. Same signature
 * as before, so all call sites keep working.
 */
export function getFaviconUrl(url: string, size: number = 32): string {
  let hostname = ''
  try {
    hostname = new URL(url).hostname.replace(/^www\./, '')
  } catch {
    hostname = ''
  }
  const letter = (hostname[0] ?? '?').toUpperCase()
  let hash = 0
  for (let i = 0; i < hostname.length; i++) hash = (hash * 31 + hostname.charCodeAt(i)) >>> 0
  const hue = hash % 360
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">` +
    `<rect width="16" height="16" rx="3" fill="hsl(${hue},45%,42%)"/>` +
    `<text x="8" y="11.5" font-size="9" font-family="system-ui,sans-serif" fill="white" text-anchor="middle">${letter}</text>` +
    `</svg>`
  void size
  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

/**

/**
 * Truncate a URL for display (keep domain + path, strip query if too long).
 */
export function truncateUrl(url: string, maxLength: number = 60): string {
  if (url.length <= maxLength) return url
  try {
    const parsed = new URL(url)
    const domain = parsed.hostname
    const path = parsed.pathname
    const display = domain + path
    if (display.length <= maxLength) return display
    return display.slice(0, maxLength - 3) + '...'
  } catch {
    return url.slice(0, maxLength - 3) + '...'
  }
}

/**
 * Folder path of an imported bookmark (stored in extra_json.folder_path).
 * Returns null when the bookmark has no folder info.
 *
 * The API returns extra_json as a JSON string (D1 stores it as TEXT), so both
 * the raw string and an already-parsed object are accepted.
 */
export function getFolderPath(extraJson: unknown): string | null {
  let parsed: unknown = extraJson
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed)
    } catch {
      return null
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null
  const folder = (parsed as Record<string, unknown>).folder_path
  return typeof folder === 'string' && folder.trim().length > 0 ? folder : null
}
